// IMPORTANT: HelpPopupContent is only for internal use.
// Consumer should only be using HelpPopup
import { createElement, Fragment, useEffect, useState, useMemo } from 'react';
import { GenericKnownObject, ResolveIcon, ResolveSVG } from '@sas/react-svg';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { useTextLocalizer, getLanguage } from '../../../locale/useTextLocalizer';
import { HelpPopupContentProps } from './';
import { setAlpha } from '../../../design-tokens';
import { getHelpCenterUrl } from '@sas/nova-core/services';
import { Icon } from '../../Icon';
import { SVG, useResolveSVGIcon } from '../../SVG';
import { useMounted } from '../../../hooks/useMounted';

import cn from './HelpPopupContent.css.json';
import './HelpPopupContent.css';

interface TopicParams {
  docsetId: string;
  docsetTarget: string;
  docsetTargetAnchor?: string;
}

export const HelpPopupContent = (props: HelpPopupContentProps) => {
  const localizeHelpContent = useTextLocalizer(props.library);
  const localizedContent = localizeHelpContent(`${props.contentKey}.help.content.txt`).replace(
    /\\([^\\])/g,
    '$1'
  );
  const { resolveSVG, resolveIcon } = useResolveSVGIcon();

  const [importedLinks, setImportedLinks] = useState<{ [key: string]: string }>({});
  const mounted = useMounted();

  useEffect(() => {
    // Parse the incoming html content so we can build the right URLs for any help center links
    const fetchHelpCenterLinks = async () => {
      const parser = new DOMParser();
      const parseResults = parser.parseFromString(localizedContent, 'text/html');
      const helpCenterLinks: Array<TopicParams> = [];
      for (const c of parseResults.body.children) {
        findHelpCenterLinks(c, helpCenterLinks);
      }
      const getKeyUrlPair = async (topicParams: TopicParams) => {
        const url = await getHelpCenterUrl({
          dataProvider: props.dataProvider,
          customParams: { locale: getLanguage(), ...topicParams },
        });
        if (url) {
          const key = getKey(
            topicParams.docsetId,
            topicParams.docsetTarget,
            topicParams.docsetTargetAnchor
          );
          return { key, url };
        }
      };
      // Go fetch the necessary data and build urls for any help center links we found
      const keyUrlPairs = await Promise.all(
        helpCenterLinks.map((topicParams) => getKeyUrlPair(topicParams))
      );
      const newImportedLinks: { [key: string]: string } = {};
      for (const keyUrlPair of keyUrlPairs) {
        if (keyUrlPair) {
          newImportedLinks[keyUrlPair.key] = keyUrlPair.url;
        }
      }
      mounted.current && setImportedLinks(newImportedLinks);
    };
    fetchHelpCenterLinks();
  }, [props.dataProvider, localizedContent, mounted]);

  const content = useMemo(() => {
    return parseContent(localizedContent, importedLinks, resolveSVG, resolveIcon);
  }, [localizedContent, importedLinks, resolveSVG, resolveIcon]);

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      mg: dt.spacing.core.m,
      'small-mg': dt.spacing.core.s,
      'tiny-mg': dt.spacing.core.xs,
      'none-mg': dt.spacing.core.none,
      pd: dt.spacing.core.m,
      'tiny-pd': dt.spacing.core.xs,
      'small-pd': dt.spacing.core.s,
      'indent-pd': dt.spacing.core.xl,
      'font-size-m': dt.fontNova.size.m,
      'line-height': dt.fontNova.lineHeight,
      'font-weight-default': dt.fontNova.weight.default,
      'font-weight-thick': dt.fontNova.weight.thick,

      fg: dt.c.colorTextDefault,
      'title-fg': dt.c.colorTextPrimary,
      'caution-fg': dt.c.colorAlert,
      'caution-bd': dt.c.colorAlert,
      'note-bd': dt.c.colorUiBorder,
      'note-fg': dt.c.colorTextLabel,
      'gen-title-bd': setAlpha(dt.c.colorUiPop, 0.5),
      'tip-bd': dt.c.colorInfo,
      'tip-title-fg': dt.c.colorInfo,
      'link-fg': dt.stateStandardNova.normal.fg,
    },
    'help-popup-content-xis'
  );

  return (
    <div style={tokens} className={cn.content} onClick={(e) => e.stopPropagation()}>
      {content}
    </div>
  );
};

const getKey = (docset: string, topic: string, anchor?: string) => {
  return `${docset}.${topic}.${anchor}`;
};

const attributesToProps = (attributes: NamedNodeMap) => {
  const props: any = {};

  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes.item(i);
    if (!attr) continue;
    if (attr.name === 'class') {
      props.className = attr.value;
    } else {
      props[attr.name] = attr.value;
    }
  }

  return props;
};

// Recursively look through content nodes for help center links
const findHelpCenterLinks = (node: Element, helpCenterLinks: Array<TopicParams>) => {
  for (const c of node.childNodes) {
    if (c instanceof Element) {
      const attributes = attributesToProps(c.attributes);
      if (c.tagName === 'A' && attributes['data-help-docset'] && attributes['data-help-topic']) {
        const docsetId = attributes['data-help-docset'];
        const docsetTarget = attributes['data-help-topic'];
        const docsetTargetAnchor = attributes['data-help-anchor'];
        if (docsetTargetAnchor) {
          helpCenterLinks.push({ docsetId, docsetTarget, docsetTargetAnchor });
        } else {
          helpCenterLinks.push({ docsetId, docsetTarget });
        }
      } else {
        findHelpCenterLinks(c, helpCenterLinks);
      }
    }
  }
  return helpCenterLinks;
};

const renderNode = (
  node: Element,
  importedLinks: { [key: string]: string },
  key?: number,
  resolveSVG?: ResolveSVG,
  resolveIcon?: ResolveIcon
) => {
  const children: Array<ReturnType<typeof createElement> | string | null> = [];

  // Loop through the content nodes looking for help center links and icons
  for (const [index, c] of node.childNodes.entries()) {
    if (c instanceof Element) {
      const attributes = attributesToProps(c.attributes);
      if (c.tagName === 'A' && attributes['data-help-docset'] && attributes['data-help-topic']) {
        // We should have imported the necessary information and built the correct url for this help center link
        // Look up that url and create a link pointing to it
        const url =
          importedLinks[
            getKey(
              attributes['data-help-docset'],
              attributes['data-help-topic'],
              attributes['data-help-anchor']
            )
          ];
        if (url) {
          children.push(
            // Links in HelpPopupContent have their own styling, use a normal link here instead of a Nova Link component
            <a {...attributes} href={url} target="_blank" key={index}>
              {c.textContent}
            </a>
          );
        } else {
          children.push(c.textContent);
        }
      } else if (c.tagName === 'BR') {
        children.push(<br />);
      } else if (c.tagName === 'SPAN' && attributes['data-icon-name']) {
        const createIconName = (n: string) => {
          return n.slice(0, 1).toUpperCase() + n.slice(1);
        };
        const name = createIconName(attributes['data-icon-name']);
        const icon = resolveIcon?.(name);
        const svg = resolveSVG?.(name);
        if (icon && icon.name !== 'GenericKnownObject') {
          children.push(<Icon icon={icon} key={index} />);
        } else if (svg && svg.name !== 'GenericKnownObject') {
          children.push(
            <SVG
              icon={svg}
              // eslint-disable-next-line @sas/i18n/jsx-to-localize
              type={name.startsWith('Thumbnail') ? 'thumbnail' : undefined}
              key={index}
            />
          );
        } else {
          children.push(<Icon icon={GenericKnownObject} key={index} />);
        }
      } else {
        children.push(renderNode(c, importedLinks, index, resolveSVG, resolveIcon));
      }
    } else if (c.textContent) {
      children.push(c.textContent);
    }
  }
  return createElement(
    node.tagName.toLowerCase(),
    { key, ...attributesToProps(node.attributes) },
    children
  );
};

// Parse the incoming html content so we can replace any help center links with nova links
const parseContent = (
  content: string,
  importedLinks: { [key: string]: string },
  resolveSVG?: ResolveSVG,
  resolveIcon?: ResolveIcon
) => {
  const parser = new DOMParser();
  const parseResults = parser.parseFromString(content, 'text/html');

  return parseResults.body.children.length === 0 ? (
    renderNode(parseResults.body, importedLinks, undefined, resolveSVG, resolveIcon)
  ) : (
    <Fragment>
      {[...parseResults.body.children].map((child, i) =>
        renderNode(child, importedLinks, i, resolveSVG, resolveIcon)
      )}
    </Fragment>
  );
};
